import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { FC } from 'react';

import { Flex, Text } from '@mantine/core';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { personalPlacesApi } from '@/shared/api/personalPlaces';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import useAppStore from '@/shared/store/app.store';
import { IRequestError } from '@/shared/types/app.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

type ActiveBookingActionsProps = {
	id: number;
	type: 'cancel' | 'end';
	opened: boolean;
	close: () => void;
	closeParent?: () => void;
};

export const PersonalPlaceActions: FC<ActiveBookingActionsProps> = ({
	id,
	type,
	opened,
	close,
	closeParent,
}) => {
	const queryClient = useQueryClient();
	const isMobile = useAppStore((state) => state.isMobile);
	const { mutate: cancelPersonalPlaceAccess, isPending: isCancelingAccess } = useMutation({
		mutationFn: personalPlacesApi.deletePersonalPlaceAccess,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['personalPlaces'] });
			useShowNotifaction({ text: 'Доступ к месту отменен', type: 'success' });
			close();
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({ text: error.response?.data?.message ?? '', type: 'error' });
		},
	});

	const { mutate: finishPersonalPlaceAccess, isPending: isFinishingAccess } = useMutation({
		mutationFn: personalPlacesApi.finishPersonalPlaceAccess,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['personalPlaces'] });
			useShowNotifaction({ text: 'Доступ к месту завершен', type: 'success' });
			close();
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({ text: error.response?.data?.message ?? '', type: 'error' });
		},
	});

	const handleEndBooking = () => {
		if (type === 'cancel') {
			cancelPersonalPlaceAccess({ id });
		} else {
			finishPersonalPlaceAccess({ id, finish: dayjs().toISOString() });
		}

		if (closeParent) {
			close();
			closeParent();
			useShowNotifaction({
				text: `Доступ к месту ${type === 'cancel' ? 'отменен' : 'завершен'}`,
				type: 'success',
			});
		}
	};

	if (isMobile)
		return (
			<BottomSheet
				opened={opened}
				onClose={close}
				title={
					<Text ta="center" fz={22} fw={800}>
						{type === 'cancel' ? 'Отмена' : 'Завершение'}
					</Text>
				}
				buttons={[
					{
						children: `${type === 'cancel' ? 'Отменить' : 'Завершить'} доступ`,
						variant: 'default',
						loading: isCancelingAccess || isFinishingAccess,
						onClick: handleEndBooking,
					},
					{
						children: 'Закрыть',
						onClick: close,
					},
				]}
				buttonsDirection="column"
				withCloseButton={false}
				withBackButton={false}
			>
				<Flex direction="column" align="center" justify="center" gap={8}>
					<Text ta="center" fz={20} fw={800}>
						Вы уверены, что хотите {type === 'cancel' ? 'отменить' : 'завершить'} предоставление
						доступа?
					</Text>
				</Flex>
			</BottomSheet>
		);

	return (
		<ModalSheet
			opened={opened}
			onClose={close}
			title={
				<Text ta="center" fz={22} fw={800}>
					{type === 'cancel' ? 'Отмена' : 'Завершение'}
				</Text>
			}
			buttons={[
				{
					children: `${type === 'cancel' ? 'Отменить' : 'Завершить'} доступ`,
					variant: 'default',
					loading: isCancelingAccess || isFinishingAccess,
					onClick: handleEndBooking,
				},
				{
					children: 'Закрыть',
					onClick: close,
				},
			]}
			size="lg"
			centered
		>
			<Flex direction="column" align="center" justify="center" gap={8}>
				<Text ta="center" fz={20} fw={800}>
					Вы уверены, что хотите {type === 'cancel' ? 'отменить' : 'завершить'} предоставление
					доступа?
				</Text>
			</Flex>
		</ModalSheet>
	);
};
